import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { creditApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Wallet, Plus, Loader2, FileText, PiggyBank } from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TXN_TYPES = [['CHARGE', 'Charge'], ['PAYMENT', 'Payment'], ['DEPOSIT', 'Deposit (advance)'], ['CREDIT_NOTE', 'Credit Note'], ['ADJUSTMENT', 'Adjustment']];

export default function CreditDepositsPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [accounts, setAccounts] = useState([]);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acctDialog, setAcctDialog] = useState(false);
  const [stmt, setStmt] = useState(null);
  const [entryFor, setEntryFor] = useState(null);

  const load = useCallback(async () => {
    if (!pid) return;
    try {
      const [a, g] = await Promise.all([creditApi.listAccounts(pid), creditApi.aging(pid)]);
      setAccounts(a.data || []); setAging(g.data);
    } catch (e) { toast.error('Failed to load credit data'); }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const openStatement = async (id) => {
    try { const r = await creditApi.statement(id); setStmt(r.data); }
    catch (e) { toast.error('Failed to load statement'); }
  };

  if (!currentProperty) return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;

  const t = aging?.totals || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-teal-600 text-white flex items-center justify-center"><Wallet className="h-6 w-6" /></div>
          <div><h1 className="text-2xl font-bold text-slate-900">Credit &amp; Deposits</h1><p className="text-sm text-slate-500">Credit customers, advance deposits &amp; receivables</p></div>
        </div>
        <Button onClick={() => setAcctDialog(true)}><Plus className="h-4 w-4 mr-2" /> New Customer</Button>
      </div>

      {/* Aging summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Outstanding</p><p className="text-lg font-semibold">{ETB(aging?.total_outstanding)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Deposits Held</p><p className="text-lg font-semibold text-teal-700">{ETB(aging?.total_deposits_held)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Current</p><p className="text-sm font-semibold">{ETB(t.current)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">31–60</p><p className="text-sm font-semibold">{ETB(t.d30)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">61–90</p><p className="text-sm font-semibold">{ETB(t.d60)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">91–120</p><p className="text-sm font-semibold">{ETB(t.d90)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">120+</p><p className="text-sm font-semibold text-red-600">{ETB(t.over90)}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Credit Customers</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Customer</TableHead><TableHead>Company</TableHead><TableHead className="text-right">Credit Limit</TableHead><TableHead className="text-right">Balance</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                : accounts.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No credit customers yet.</TableCell></TableRow>
                : accounts.map((a) => {
                  const bal = a.current_balance || 0;
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">{a.code}</TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>{a.company_name || '—'}</TableCell>
                      <TableCell className="text-right">{ETB(a.credit_limit)}</TableCell>
                      <TableCell className={`text-right font-medium ${bal > 0 ? 'text-red-600' : bal < 0 ? 'text-teal-700' : ''}`}>
                        {ETB(Math.abs(bal))} {bal > 0 ? 'Dr' : bal < 0 ? 'Cr' : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openStatement(a.id)}><FileText className="h-4 w-4 mr-1" /> Statement</Button>
                        <Button variant="ghost" size="sm" onClick={() => setEntryFor(a)}><PiggyBank className="h-4 w-4 mr-1" /> Entry</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AccountDialog open={acctDialog} onOpenChange={setAcctDialog} pid={pid} onSaved={() => { setAcctDialog(false); load(); }} />
      <EntryDialog account={entryFor} onClose={() => setEntryFor(null)} onSaved={() => { setEntryFor(null); load(); }} />
      <StatementDialog statement={stmt} onClose={() => setStmt(null)} />
    </div>
  );
}

function AccountDialog({ open, onOpenChange, pid, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: '', company_name: '', contact_email: '', contact_phone: '', credit_limit: '', opening_balance: '' }); }, [open]);
  const save = async () => {
    if (!f.name?.trim()) { toast.error('Enter a name'); return; }
    try {
      setSaving(true);
      await creditApi.createAccount(pid, { name: f.name, company_name: f.company_name || null, contact_email: f.contact_email || null, contact_phone: f.contact_phone || null, credit_limit: parseFloat(f.credit_limit) || 0, opening_balance: parseFloat(f.opening_balance) || 0 });
      toast.success('Customer created'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Credit Customer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Company</Label><Input value={f.company_name || ''} onChange={(e) => setF({ ...f, company_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input value={f.contact_email || ''} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.contact_phone || ''} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Credit Limit</Label><Input type="number" value={f.credit_limit || ''} onChange={(e) => setF({ ...f, credit_limit: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Opening Balance</Label><Input type="number" value={f.opening_balance || ''} onChange={(e) => setF({ ...f, opening_balance: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntryDialog({ account, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (account) setF({ txn_type: 'CHARGE', amount: '', txn_date: new Date().toISOString().slice(0, 10), reference: '', description: '' }); }, [account]);
  const save = async () => {
    if (!(parseFloat(f.amount) > 0)) { toast.error('Enter an amount'); return; }
    try {
      setSaving(true);
      const r = await creditApi.addEntry(account.id, { txn_type: f.txn_type, amount: parseFloat(f.amount), txn_date: f.txn_date, reference: f.reference || null, description: f.description || null });
      toast.success(`Recorded — new balance ${ETB(r.data.new_balance)}`);
      if (r.data.over_limit) toast.warning('Customer is over their credit limit');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Ledger Entry</DialogTitle><DialogDescription>{account?.name}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Type</Label>
            <Select value={f.txn_type} onValueChange={(v) => setF({ ...f, txn_type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TXN_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Amount</Label><Input type="number" value={f.amount || ''} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={f.txn_date || ''} onChange={(e) => setF({ ...f, txn_date: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Reference</Label><Input value={f.reference || ''} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Input value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Record</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementDialog({ statement, onClose }) {
  const s = statement;
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Statement — {s?.account?.name}</DialogTitle>
          <DialogDescription>{s?.account?.company_name}</DialogDescription>
        </DialogHeader>
        {s && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div className="rounded bg-slate-50 p-2"><p className="text-[11px] text-slate-500">Charges</p><p className="font-semibold">{ETB(s.summary.total_charges)}</p></div>
              <div className="rounded bg-slate-50 p-2"><p className="text-[11px] text-slate-500">Payments</p><p className="font-semibold">{ETB(s.summary.total_payments)}</p></div>
              <div className="rounded bg-slate-50 p-2"><p className="text-[11px] text-slate-500">Deposits</p><p className="font-semibold">{ETB(s.summary.total_deposits)}</p></div>
              <div className="rounded bg-slate-50 p-2"><p className="text-[11px] text-slate-500">Balance</p><p className="font-semibold">{ETB(s.summary.current_balance)}</p></div>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {s.entries.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">No entries.</TableCell></TableRow>
                  : s.entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.txn_date}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{e.txn_type}</Badge></TableCell>
                      <TableCell className="max-w-[200px] truncate">{e.description}</TableCell>
                      <TableCell className="text-right">{e.debit ? ETB(e.debit) : '—'}</TableCell>
                      <TableCell className="text-right">{e.credit ? ETB(e.credit) : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{ETB(e.balance)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
